import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import RankingSaldo from "../components/ui/RankingSaldo";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";
import { baixarCsv } from "../utils/exportar";
import { formatarHoras } from "../utils/tempo";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function navBtn() {
  return { background: "transparent", border: "none", fontSize: 18, color: COLORS.textMid, cursor: "pointer", padding: "0 4px", lineHeight: 1 };
}

function selectStyle() {
  return { border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "8px 12px", fontSize: 13, background: COLORS.surface, color: COLORS.text };
}

export default function FechamentoMensal() {
  const { token } = useAuth();
  const [ref, setRef] = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() + 1 }; });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroEquipe, setFiltroEquipe] = useState("");
  // Lista de equipes pro dropdown vem de /funcionarios (sempre completa) em
  // vez de dados.funcionarios (que já pode estar filtrado por equipe) —
  // senão, escolher uma equipe faria as outras opções somem do próprio filtro.
  const [equipesDisponiveis, setEquipesDisponiveis] = useState([]);

  // Um filtro por coluna, embutido no cabeçalho da tabela — client-side,
  // sobre `dados.funcionarios` (que já veio filtrado por equipe do backend).
  const [buscaNome, setBuscaNome] = useState("");
  const [filtroFaltas, setFiltroFaltas] = useState("");
  const [filtroFerias, setFiltroFerias] = useState("");
  const [filtroAtestados, setFiltroAtestados] = useState("");
  const [filtroAtrasos, setFiltroAtrasos] = useState("");
  const [filtroSaldo, setFiltroSaldo] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/funcionarios", undefined, token).then((lista) => {
      setEquipesDisponiveis([...new Set(lista.map((f) => f.equipe).filter(Boolean))].sort());
    });
  }, [token]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ ano: ref.ano, mes: ref.mes });
      if (filtroEquipe) query.set("equipe", filtroEquipe);
      const data = await apiFetch("GET", `/api/relatorios/fechamento-mensal?${query}`, undefined, token);
      setDados(data);
    } finally {
      setLoading(false);
    }
  }, [ref, filtroEquipe, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const mudarMes = (delta) => {
    let { ano, mes } = ref;
    mes += delta;
    if (mes > 12) { mes = 1; ano += 1; }
    if (mes < 1) { mes = 12; ano -= 1; }
    setRef({ ano, mes });
  };

  const exportarCsv = () => {
    if (!dados) return;
    const cabecalhos = ["Funcionário", "Cargo", "Setor", "Dias trabalhados", "Faltas", "Férias", "Atestados", "Atrasos (qtd)", "Atrasos (min)", "Horas trabalhadas", "Horas extras aprovadas", "Saldo final"];
    const linhas = dados.funcionarios.map((f) => [
      f.nome,
      f.cargo || "",
      f.equipe || "",
      f.dias_trabalhados,
      f.dias_falta,
      f.dias_ferias,
      f.dias_atestado,
      f.atrasos_qtd,
      f.atrasos_minutos,
      formatarHoras(f.horas_trabalhadas),
      formatarHoras(f.horas_extra_aprovadas),
      formatarHoras(f.saldo_final),
    ]);
    baixarCsv(`fechamento-mensal-${String(ref.mes).padStart(2, "0")}-${ref.ano}.csv`, cabecalhos, linhas);
  };

  const funcionariosFiltrados = (dados?.funcionarios || []).filter((f) => {
    if (buscaNome && !f.nome.toLowerCase().includes(buscaNome.toLowerCase())) return false;
    if (filtroFaltas === "com" && !(f.dias_falta > 0)) return false;
    if (filtroFaltas === "sem" && f.dias_falta > 0) return false;
    if (filtroFerias === "com" && !(f.dias_ferias > 0)) return false;
    if (filtroFerias === "sem" && f.dias_ferias > 0) return false;
    if (filtroAtestados === "com" && !(f.dias_atestado > 0)) return false;
    if (filtroAtestados === "sem" && f.dias_atestado > 0) return false;
    if (filtroAtrasos === "com" && !(f.atrasos_qtd > 0)) return false;
    if (filtroAtrasos === "sem" && f.atrasos_qtd > 0) return false;
    if (filtroSaldo === "positivo" && !(f.saldo_final > 0)) return false;
    if (filtroSaldo === "negativo" && !(f.saldo_final < 0)) return false;
    if (filtroSaldo === "zero" && f.saldo_final !== 0) return false;
    return true;
  });
  const filtrosColunaAtivos = Boolean(buscaNome || filtroFaltas || filtroFerias || filtroAtestados || filtroAtrasos || filtroSaldo);
  const limparFiltrosColuna = () => {
    setBuscaNome(""); setFiltroFaltas(""); setFiltroFerias(""); setFiltroAtestados(""); setFiltroAtrasos(""); setFiltroSaldo("");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Fechamento Mensal
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Resumo do mês por funcionário — pronto pra RH ou contabilidade conferir.
          </p>
        </div>

        <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: COLORS.surface, border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "6px 10px" }}>
            <button onClick={() => mudarMes(-1)} style={navBtn()}>‹</button>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, minWidth: 130, textAlign: "center" }}>
              {MESES[ref.mes - 1]} {ref.ano}
            </span>
            <button onClick={() => mudarMes(1)} style={navBtn()}>›</button>
          </div>
          {equipesDisponiveis.length > 0 && (
            <select value={filtroEquipe} onChange={(e) => setFiltroEquipe(e.target.value)} style={selectStyle()}>
              <option value="">Todos os setores</option>
              {equipesDisponiveis.map((eq) => (
                <option key={eq} value={eq}>{eq}</option>
              ))}
            </select>
          )}
          {dados && dados.funcionarios.length > 0 && (
            <>
              <Btn variant="secondary" onClick={exportarCsv}>Baixar CSV</Btn>
              <Btn variant="secondary" onClick={() => window.print()}>Imprimir / PDF</Btn>
            </>
          )}
        </div>
      </div>

      {dados && (dados.maiores_saldos.length > 0 || dados.menores_saldos.length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <RankingSaldo maiores={dados.maiores_saldos} menores={dados.menores_saldos} />
        </div>
      )}

      {dados && dados.funcionarios.length > 0 && (
        <BarraResumoFiltro
          total={funcionariosFiltrados.length}
          totalGeral={dados.funcionarios.length}
          unidade="funcionário(s)"
          filtrosAtivos={filtrosColunaAtivos}
          onLimpar={limparFiltrosColuna}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : !dados || dados.funcionarios.length === 0 ? (
          <EstadoVazio icon="barChart" texto="Nenhum funcionário ativo neste período ainda." />
        ) : (
          <>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", fontSize: 12.5 }}>
              <span style={{ color: COLORS.textMid }}>
                Total saldos positivos: <strong style={{ color: COLORS.success }}>{formatarHoras(dados.total_saldos_positivos)}</strong>
              </span>
              <span style={{ color: COLORS.textMid }}>
                Total saldos negativos: <strong style={{ color: COLORS.danger }}>{formatarHoras(dados.total_saldos_negativos)}</strong>
              </span>
            </div>
          {funcionariosFiltrados.length === 0 ? (
            <EstadoVazio icon="barChart" texto="Nenhum funcionário encontrado com esse filtro." />
          ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <ThFiltroTexto label="Funcionário" value={buscaNome} onChange={setBuscaNome} placeholder="Buscar nome..." minWidth={170} />
                  <ThSimples label="Dias trab." />
                  <ThFiltroSelect
                    label="Faltas" value={filtroFaltas} onChange={setFiltroFaltas} minWidth={110}
                    opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com falta" }, { value: "sem", label: "Sem falta" }]}
                  />
                  <ThFiltroSelect
                    label="Férias" value={filtroFerias} onChange={setFiltroFerias} minWidth={110}
                    opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com férias" }, { value: "sem", label: "Sem férias" }]}
                  />
                  <ThFiltroSelect
                    label="Atestados" value={filtroAtestados} onChange={setFiltroAtestados} minWidth={120}
                    opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com atestado" }, { value: "sem", label: "Sem atestado" }]}
                  />
                  <ThFiltroSelect
                    label="Atrasos" value={filtroAtrasos} onChange={setFiltroAtrasos} minWidth={120}
                    opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com atraso" }, { value: "sem", label: "Sem atraso" }]}
                  />
                  <ThSimples label="Horas trabalhadas" />
                  <ThSimples label="Horas extras" />
                  <ThFiltroSelect
                    label="Saldo final" value={filtroSaldo} onChange={setFiltroSaldo} minWidth={130}
                    opcoes={[{ value: "", label: "Todos" }, { value: "positivo", label: "Positivo" }, { value: "negativo", label: "Negativo" }, { value: "zero", label: "Zerado" }]}
                  />
                </tr>
              </thead>
              <tbody>
                {funcionariosFiltrados.map((f) => (
                  <tr key={f.funcionario_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 700, color: COLORS.text }}>{f.nome}</div>
                      {(f.cargo || f.equipe) && (
                        <div style={{ fontSize: 11.5, color: COLORS.textLight }}>
                          {[f.cargo, f.equipe].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, color: COLORS.textMid, fontVariantNumeric: "tabular-nums" }}>{f.dias_trabalhados}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, fontVariantNumeric: "tabular-nums", color: f.dias_falta > 0 ? COLORS.danger : COLORS.textMid, fontWeight: f.dias_falta > 0 ? 700 : 400 }}>{f.dias_falta}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, color: COLORS.textMid, fontVariantNumeric: "tabular-nums" }}>{f.dias_ferias}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, color: COLORS.textMid, fontVariantNumeric: "tabular-nums" }}>{f.dias_atestado}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, fontVariantNumeric: "tabular-nums", color: f.atrasos_qtd > 0 ? COLORS.warning : COLORS.textMid, fontWeight: f.atrasos_qtd > 0 ? 700 : 400, whiteSpace: "nowrap" }}>
                      {f.atrasos_qtd > 0 ? `${f.atrasos_qtd}x (${f.atrasos_minutos}min)` : "—"}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, color: COLORS.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatarHoras(f.horas_trabalhadas)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13.5, color: COLORS.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatarHoras(f.horas_extra_aprovadas)}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <Badge tone={f.saldo_final > 0 ? "success" : f.saldo_final < 0 ? "danger" : "neutral"}>
                        {formatarHoras(f.saldo_final)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          </>
        )}
      </Card>
    </div>
  );
}
